import axios from "axios";
import apiCache from "./apiCache";

console.log(process.env.REACT_APP_API_URL);

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
  },
});

// ─── HELPER: lê o token do redux-persist (localStorage) ────────────────────
function getTokenFromStorage() {
  try {
    const persisted = localStorage.getItem("persist:Oxe_Tech");
    if (!persisted) return null;
    const outer = JSON.parse(persisted);
    if (!outer?.auth) return null;
    const auth = JSON.parse(outer.auth);
    return auth?.token || null;
  } catch (_) {
    return null;
  }
}

// ─── REQUEST INTERCEPTOR: injeta token se ainda não estiver no header ───────
api.interceptors.request.use((config) => {
  if (!config.headers.Authorization && !api.defaults.headers.Authorization) {
    const token = getTokenFromStorage();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// ─── REQUEST INTERCEPTOR: retorna cache se disponível ───────────────────────
api.interceptors.request.use((config) => {
  // Só cacheia GET
  if (config.method !== "get") return config;

  // Permite desabilitar cache por request: api.get(url, { cache: false })
  if (config.cache === false) return config;

  const key = apiCache.buildKey(config);
  const cached = apiCache.get(key);

  if (cached) {
    if (cached.isFresh) {
      // Cache fresco: retorna imediatamente sem fazer request
      config.__fromCache = true;
      config.adapter = () =>
        Promise.resolve({
          data: cached.data,
          status: 200,
          statusText: "OK (cache)",
          headers: cached.headers || {},
          config,
          request: {},
        });
      return config;
    }

    // Cache stale: retorna cache imediatamente, mas revalida em background
    if (!apiCache.isRevalidating(key)) {
      apiCache.markRevalidating(key);

      // Faz request em background para atualizar o cache
      // Usa URL completa para garantir que funcione com o proxy
      const fullUrl = (config.baseURL || "").replace(/\/$/, "") + (config.url || "");
      const bgConfig = {
        ...config,
        baseURL: undefined,
        url: fullUrl,
        adapter: undefined,
        __bgRevalidation: true,
      };
      axios(bgConfig)
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            apiCache.set(key, response.data, response.headers);
          }
        })
        .catch(() => {
          // Silencia erros de revalidação em background
        })
        .finally(() => {
          apiCache.unmarkRevalidating(key);
        });
    }

    // Enquanto isso, retorna do cache stale
    config.__fromCache = true;
    config.adapter = () =>
      Promise.resolve({
        data: cached.data,
        status: 200,
        statusText: "OK (stale cache)",
        headers: cached.headers || {},
        config,
        request: {},
      });
    return config;
  }

  return config;
});

// ─── RESPONSE INTERCEPTOR: salva respostas GET no cache ─────────────────────
api.interceptors.response.use(
  (response) => {
    // Salva no cache apenas respostas GET bem-sucedidas que não vieram do cache
    if (
      response.config.method === "get" &&
      response.config.cache !== false &&
      !response.config.__fromCache &&
      !response.config.__bgRevalidation &&
      response.status >= 200 &&
      response.status < 300
    ) {
      const key = apiCache.buildKey(response.config);
      apiCache.set(key, response.data, response.headers);
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ─── RESPONSE INTERCEPTOR: detecta token expirado e emite evento global ─────
let _sessionExpiredFired = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !_sessionExpiredFired) {
      _sessionExpiredFired = true;
      window.dispatchEvent(new CustomEvent("auth:session-expired"));
      setTimeout(() => { _sessionExpiredFired = false; }, 5000);
    }
    return Promise.reject(error);
  }
);

// ─── RESPONSE INTERCEPTOR: invalida cache em operações de escrita ───────────
api.interceptors.response.use(
  (response) => {
    const method = response.config.method;

    // POST, PUT, PATCH, DELETE invalidam cache relacionado
    if (["post", "put", "patch", "delete"].includes(method)) {
      const url = response.config.url || "";

      // Extrai o path base (ex: "/oxetechedu/escola/123" → "/oxetechedu/escola")
      const segments = url.split("/").filter(Boolean);
      if (segments.length >= 2) {
        const basePath = "/" + segments.slice(0, 2).join("/");
        apiCache.invalidate(basePath);
      }

      // Também invalida a URL exata
      apiCache.invalidate(url);
    }

    return response;
  },
  (error) => {
    // Também invalida cache em caso de erro em operações de escrita
    if (error.config) {
      const method = error.config.method;
      if (["post", "put", "patch", "delete"].includes(method)) {
        const url = error.config.url || "";
        apiCache.invalidate(url);
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Exporta o cache para uso direto quando necessário
export { apiCache };